import { Link, useNavigate } from "react-router-dom";
import styles from "./signup.module.css";
import { useEffect, useRef, useState } from "react";
import Loader from "../../../admin/components/Loader";
import { ArrowLeft } from "react-feather";
import axios from "axios";
import { StorageSetItem, STORAGE_KEYS } from "../../../admin/components/StorageComp";
import { buildAuthCookie } from "../login/Login";
import { formatGBP } from "../../../admin/components/Features";
import { Controller, useForm } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { Checkbox, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { CountryList } from "../../../admin/components/Constants";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import SharedPasswordFields, { publicAccountsLookup } from "../../../admin/components/SharedPasswordFields";
import VerifyCodeBox from "../customer-signup/VerifyCodeBox";

const DOMAIN_SUFFIX = "." + process.env.REACT_APP_BASE_DOMAIN;

const COMPANY_SUFFIX_WORDS = [
  "private", "ltd", "limited", "llc", "inc", "incorporated",
  "corp", "corporation", "co", "company", "pvt", "plc",
  "shipping", "logistics", "group", "holdings", "enterprises",
]

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [domainStatus, setDomainStatus] = useState(null);
  // Set when the email already has accounts and the new password needs an emailed code
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const domainManuallyEdited = useRef(false);
  const domainCheckTimer = useRef(null);

  const { websiteData } = useWebsite();

  useSiteBranding("Sign Up", websiteData);

  const themePrimaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.primaryColor ? websiteData?.home_config?.primaryColor : "#ef233c";
  const themeSecondaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.secondaryColor ? websiteData?.home_config?.secondaryColor : "#0a2a52";

  const { register, handleSubmit, setValue, control, watch, formState: { errors } } = useForm({
    mode: "onChange",
  });

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("company_address", place.formatted_address);

      if (place.address_components) {
        let address = "";
        let city = "";
        let postcode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = component.long_name;
          }

          if (types.includes("route")) {
            if (address) {
              address += " " + component.long_name;
            } else {
              address = component.long_name;
            }
          }

          if (types.includes("postal_town")) {
            city = component.long_name;
          }

          if (types.includes("locality") && !city) {
            city = component.long_name;
          }

          if (types.includes("postal_code")) {
            postcode = component.long_name;
          }

        });

        setValue("address", address);
        setValue("city", city);
        setValue("zip", postcode);
      }

      console.log(place);
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: 'GB' },
    },
  });

  // Pricing panel reads the live Basic/add-on prices set by the platform admin
  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/public/pricing").then((response) => {
      if (response.data.status === "SUCCESS") setPricing(response.data.data);
    }).catch(() => { });
  }, []);

  // A code was sent for this email + password; changing either needs a new one
  const watchEmail = watch("email");
  const watchPassword = watch("password");
  const watchPasswordMode = watch("password_mode");
  useEffect(() => {
    setVerifyMsg("");
    setVerificationCode("");
  }, [watchEmail, watchPassword, watchPasswordMode]);

  async function onSubmit(data, withCode) {
    if (withCode && verificationCode.length !== 6) {
      setErrorMsg("Enter the 6-digit code we sent to your email.");
      return;
    }

    data = { ...data, url_token: data?.domain, domain: data?.domain ? `${data.domain}${DOMAIN_SUFFIX}` : "", website_template_id: 1, ...(withCode ? { verification_code: verificationCode } : {}) };

    setLoading(true);
    setErrorMsg("");

    return axios.post(process.env.REACT_APP_API_URL + "v1/signup", data).then((response) => {
      if (response.data.status === "VERIFY") {
        setLoading(false);
        setVerificationCode("");
        setVerifyMsg(response.data.message);
        return;
      }
      if (response.data.status === "SUCCESS") {
        // The new password now applies to their other accounts: show it before leaving
        if (response.data.shared_password_accounts > 0) {
          window.alert(response.data.message);
        }
        // Signup starts the free trial and signs the admin in; they land on Service Add-ons
        const auth = response.data.auth;
        if (!auth) {
          setLoading(false);
          navigate("/sign-in");
          return;
        }
        StorageSetItem(STORAGE_KEYS.AUTH, buildAuthCookie(auth), 24 * 60 * 60 * 1000);

        const companyDomain = auth?.company?.domain;
        if (companyDomain) {
          const redirectUrl = /^https?:\/\//i.test(companyDomain) ? companyDomain : `${window.location.protocol}//${companyDomain}`;
          window.location.href = `${redirectUrl}/company-profile?tab=addons`;
        } else {
          navigate("/company-profile?tab=addons");
        }
        return;
      }
      setLoading(false);
      if (response.data.status === "ERROR") {
        setErrorMsg(response?.data?.message);
      }
    }).catch((error) => {
      if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
        setErrorMsg(error?.response?.data?.message);
      }
      setLoading(false);
    });
  }

  const slugify = (value) => {
    if (!value) return "";

    const words = value.trim().split(/\s+/).map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, "")).filter(Boolean);

    // Drop trailing/noise words like "shipping", "private", "ltd" etc.
    const meaningfulWords = words.filter((w) => !COMPANY_SUFFIX_WORDS.includes(w));

    const base = meaningfulWords.length > 0 ? meaningfulWords : words;

    // Keep it short — just the first meaningful word
    return base[0] || "";
  }

  const companyNameValue = watch("company_name");

  useEffect(() => {
    if (domainManuallyEdited.current) return;
    const slug = slugify(companyNameValue || "");
    setValue("domain", slug, { shouldValidate: true });
    checkDomainAvailability(slug);
  }, [companyNameValue]);

  const checkDomainAvailability = (value) => {
    if (domainCheckTimer.current) clearTimeout(domainCheckTimer.current);

    if (!value || value.length < 3) {
      setDomainStatus(null);
      return;
    }

    setDomainStatus("checking");

    domainCheckTimer.current = setTimeout(() => {
      axios.post(process.env.REACT_APP_API_URL + "v1/check-domain", {
        domain: `${value}${DOMAIN_SUFFIX}`,
      }).then((response) => {
        if (response.data.status === "SUCCESS") {
          setDomainStatus(response.data.data?.is_available ? "available" : "taken");
        } else {
          setDomainStatus("error");
        }
      }).catch(() => {
        setDomainStatus("error");
      });
    }, 500);
  }

  const handleDomainChange = (e, onChange) => {
    domainManuallyEdited.current = true;
    let val = e.target.value.toLowerCase();
    val = val.replace(/[^a-z0-9-]/g, "");
    val = val.replace(/^-+/, "");
    onChange(val);
    checkDomainAvailability(val);
  }

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={styles.MainDiv} style={{ "--primary-color": themePrimaryColor, "--secondary-color": themeSecondaryColor }}>
      <div className={styles.LoginFormSec}>
        <div className={styles.LohinForm}>
          <Link to={"/"} className={styles.BackHomeBU}>
            <ArrowLeft size={18} />
            Back to home
          </Link>

          <h2 className={styles.Heading}>Sign Up</h2>
          <p className={styles.LoginformSmTitle}>Start your {pricing?.trial_days ?? 7}-day free trial. No card needed.</p>

          <form onSubmit={handleSubmit((data) => onSubmit(data, !!verifyMsg))}>
            <input type="hidden" {...register("address")} />
            <input type="hidden" {...register("city")} />
            <input type="hidden" {...register("zip")} />

            {errorMsg && (<p className={styles.ErrorM2}>{errorMsg}</p>)}

            <div className={styles.FormRow}>
              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("company_name", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Company Name*"
                    variant="outlined"
                  />)}
                />
                {errors.company_name && (<span className={styles.ErrorM}>{errors?.company_name?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("domain", {
                    required: "This field is Required",
                    minLength: { value: 3, message: "Must be at least 3 characters" },
                    pattern: {
                      value: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                      message: "Lowercase letters, numbers and hyphens only",
                    },
                    validate: async (value) => {
                      if (!value) return true; // required rule already covers empty
                      try {
                        const response = await axios.post(process.env.REACT_APP_API_URL + "v1/check-domain", {
                          domain: `${value}${DOMAIN_SUFFIX}`,
                        });
                        if (response.data.status === "SUCCESS" && response.data.data?.is_available) {
                          return true;
                        }
                        return "This domain is already taken";
                      } catch (error) {
                        return "Could not verify domain availability, please try again";
                      }
                    },
                  })}
                  render={({ field }) => (<TextField
                    {...field}
                    onChange={(e) => handleDomainChange(e, field.onChange)}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Domain*"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (<InputAdornment position="end" className={styles.DomainSuffix}>
                        {"." + process.env.REACT_APP_BASE_DOMAIN}
                      </InputAdornment>),
                    }}
                  />)}
                />
                {errors.domain ? (<span className={styles.ErrorM}>{errors?.domain?.message}</span>) : domainStatus === "checking"
                  ? (<span className={styles.FieldHint}>Checking availability…</span>) : domainStatus === "available" ? (<span className={styles.FieldSuccess}>This domain is available</span>) : domainStatus === "taken" ? (<span className={styles.ErrorM}>This domain is already taken</span>) : (<span className={styles.FieldHint}>Your booking portal will be available at this address</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("company_address", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Company Address*"
                    variant="outlined"
                    placeholder=""
                    inputRef={ref}
                  />)}
                />
                {errors.company_address && (<span className={styles.ErrorM}>{errors?.company_address?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("name", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Owner Name*"
                    variant="outlined"
                  />)}
                />
                {errors.name && (<span className={styles.ErrorM}>{errors?.name?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("email", {
                    required: "This field is Required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Email*"
                    variant="outlined"
                  />)}
                />
                {errors.email && (<span className={styles.ErrorM}>{errors.email.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("phone", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Phone*"
                    variant="outlined"
                  />)}
                />
                {errors.phone && (<span className={styles.ErrorM}>{errors.phone.message}</span>)}
              </div>

              <SharedPasswordFields
                variant="public"
                control={control}
                watch={watch}
                errors={errors}
                lookupAccounts={publicAccountsLookup()}
                groupClassName={styles.FormGroupHalf}
                fullGroupClassName={styles.FormGroupFull}
                errorClassName={styles.ErrorM}
                inputClasses={{ root: styles.inputRoot }}
              />

              <div className={styles.FormGroupHalf}>
                <FormControl fullWidth>
                  <InputLabel id="pickup-country-label">Pickup Country*</InputLabel>
                  <Controller
                    control={control}
                    {...register("pickup_country", { required: "This field is required" })}
                    render={({ field }) => (<Select
                      labelId="pickup-country-label"
                      label="Pickup Country*"
                      classes={{ root: styles.selectRoot }}
                      {...field}
                    >
                      <MenuItem value={"GB"}>United Kingdom</MenuItem>
                    </Select>)}
                  />
                </FormControl>
                {errors.pickup_country && (<span className={styles.ErrorM}>{errors?.pickup_country?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <FormControl fullWidth>
                  <InputLabel id="delivery-country-label">Delivery Country*</InputLabel>
                  <Controller
                    control={control}
                    {...register("delivery_country", { required: "This field is required" })}
                    render={({ field }) => (<Select
                      labelId="delivery-country-label"
                      label="Delivery Country*"
                      classes={{ root: styles.selectRoot }}
                      {...field}
                    >
                      {CountryList?.map((i) => {
                        return (<MenuItem key={i?.value} value={i?.value}>{i?.label}</MenuItem>);
                      })}
                    </Select>)}
                  />
                </FormControl>
                {errors.delivery_country && (<span className={styles.ErrorM}>{errors?.delivery_country?.message}</span>)}
              </div>

              <div className={styles.FormGroupFull}>
                <FormControl fullWidth className={styles.TermsRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('terms', { required: 'You must accept the terms and conditions' })}
                        error={!!errors.terms}
                      />
                    }
                    label={<>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" className={styles.ForhotPassText}>
                        Terms and Conditions
                      </Link>
                    </>
                    }
                  />
                </FormControl>
                {errors.terms && (<span className={styles.ErrorM}>{errors?.terms?.message}</span>)}
              </div>
            </div>

            {verifyMsg && (<VerifyCodeBox message={verifyMsg} code={verificationCode} setCode={setVerificationCode} onResend={handleSubmit((data) => onSubmit(data, false))} />)}
            {errorMsg && (<p className={styles.ErrorM2} role="alert">{errorMsg}</p>)}

            <button type="submit" className={styles.LoginBU}>
              <span>{verifyMsg ? "Verify & Register" : "Register"}</span>
            </button>

            <p className={styles.LoginFormSText}>
              Already have an account? <Link to={"/sign-in"} className={styles.ForhotPassText}>Login</Link>
            </p>
          </form>


        </div>
      </div>

      <div className={styles.LoginBGSec}>
        <img src="/images/login-side-banner.png" alt="Logistics" />
        <div className={styles.Overlay}></div>

        <div className={styles.PlanTeaser}>
          <h3>One simple plan. Add only what you need.</h3>
          <p>Start with Basic and switch add-ons on or off any time. No setup fees, no long-term contracts.</p>
        </div>

        <div className={styles.TeaserPlanWrap}>
          <div className={styles.TeaserPlanItem}>
            <div>
              <p className={styles.TeaserPlanName}>Free Trial</p>
              <p className={styles.TeaserPlanDesc}>{pricing?.trial_days ?? 7} days, add-ons included</p>
            </div>
            <div className={styles.TeaserPlanPrice}>£0</div>
          </div>

          <div className={`${styles.TeaserPlanItem} ${styles.TeaserFeatured}`}>
            <div>
              <p className={styles.TeaserPlanName}>Basic plan</p>
              <p className={styles.TeaserPlanDesc}>Bookings, customers, drivers, items, reports</p>
            </div>
            <div className={styles.TeaserPlanPrice}>{pricing ? formatGBP(pricing.basic_price) : "…"}<span>/mo</span></div>
          </div>

          <div className={styles.TeaserPlanItem}>
            <div>
              <p className={styles.TeaserPlanName}>Add-ons</p>
              <p className={styles.TeaserPlanDesc}>{pricing?.addons?.length ? `${pricing.addons.length} optional` : "Optional"} features, e.g. live tracking, shipping, apps</p>
            </div>
            <div className={styles.TeaserPlanPrice}>{pricing ? `from ${formatGBP(Math.min(...pricing.addons.map((a) => a.monthly_price)))}` : "…"}<span>/mo each</span></div>
          </div>
        </div>
      </div>
    </div>
  </>);
}

export default Signup;